const telemedUrl = process.env.REACT_APP_TELEMED_URL;
const filesDotComUrl = process.env.REACT_APP_FILES_URL;
const awsConnectUrl = process.env.REACT_APP_AWS_CONNECT_URL;

export const PublicRoutes = {
  emailSubscription: '/email-subscription',
  emailUnsubscribed: '/email-unsubscribed',
  ssoLogin: '/sso-login',
  resetPassword: '/reset-password',
} as const;

export const Routes = {
  // General
  overviewDashboard: '/',
  login: '/login',
  notFound: '/404',
  profile: '/profile',
  sso: '/sso',

  // Activity & Reports
  activityReport: '/activity-report',
  adherenceReport: '/adherence-report',
  adherenceReportLast30Days: '/adherence-report-30-days',
  bucketReport: filesDotComUrl,
  clinicalMetrics: '/clinical-metrics',
  complianceReport: '/compliance-report',
  interactionStatus: '/interaction-status',
  interactiveTime: '/interactive-time',
  interactiveTimeDashboard: '/interactive-time-dashboard',
  patientsStatusReport: '/patients-status',
  performanceReview: '/performance-review',
  statisticsReport: '/statistics-report',
  smsDashboard: '/sms-dashboard',
  smsDashboardMetrics: '/sms-dashboard-metrics',
  smsAutoReplySettings: '/sms-autoreply-settings',
  callDashboard: '/call-dashboard',
  turkDashboard: '/turk-dashboard',
  untrackedInboundCalls: '/untracked-inbound-calls',
  newPatientsReport: '/new-patients',
  normalReadingsReport: '/normal-readings-report',

  // CCM
  ccm: '/ccm',
  ccmAgentDashboard: '/ccm/agent-dashboard',
  ccmBillingReport: '/ccm/billing-report',
  ccmBillingReportV2: '/v2/ccm/billing-report',
  ccmConsent: (patientGuid: string) => `/ccm/consent/${patientGuid}`,
  ccmInteractiveTimeReport: '/ccm/interactive-time',
  ccmOnboardingStatus: '/ccm/onboarding-status',
  ccmPatient: (patientGuid: string) => `/ccm/patient/${patientGuid}`,
  ccmStatusReport: '/ccm/status-report',
  ccmConsole: '/ccm/console',

  // APCM
  apcmConsent: (patientGuid: string) => `/apcm/consent/${patientGuid}`,
  apcmEligibilityReport: '/apcm-eligibility-report',

  // Eligibility & Billing
  billingClaimsAthena: '/billing-tools-claims-athena',
  billingClaimsEpic: '/billing-tools-claims-epic',
  eligibilityTool: '/eligibility-tool',
  eligibilityToolItems: (processGuid: string) =>
    `/eligibility-tool/${processGuid}`,
  medicareBilling: '/medicare-billing',

  // Call management
  callHistoryManagement: '/call-history-management',

  // Adherence settings
  adherenceSettingsCohortsInactiveDays:
    '/adherence-settings/cohorts-inactive-days',
  adherenceSettingsQueueLimits: '/adherence-settings/queue-limits',

  // Onboarding & Practice
  onboardingStatus: '/onboarding-status',
  practiceOverview: '/practice-overview',

  // Patient Management
  patientDetails: '/patient/details',
  patientTransfer: '/patient-transfer-tool',
  patientProfileGuid: (patientGuid: string) =>
    `/patient-profile/${patientGuid}`,
  patientGuid: (patientGuid: string) => `/patient/${patientGuid}`,
  patientReferral: 'https://www.accuhealth.tech/patientsubmission',
  servicePlans: '/service-plans',

  // Admin - Agencies
  adminAgencyList: '/admin/agencies',
  adminAgency: (agencyGuid: string) => `/admin/agency/${agencyGuid}`,
  adminAgencyAdd: '/admin/agencies/add',
  adminAgencyEdit: (agencyGuid: string) => `/admin/agency/${agencyGuid}/edit`,

  // Admin - Agency Doctor
  adminAgencyDoctorList: '/admin/agents',
  adminAgencyDoctor: (agentGuid: string) => `/admin/agent/${agentGuid}`,
  adminAgencyDoctorAdd: '/admin/agents/add',
  adminAgencyDoctorEdit: (agentGuid: string) =>
    `/admin/agent/${agentGuid}/edit`,

  // Admin - Clinics
  adminClinics: '/admin/clinics',
  adminClinic: (clinicGuid: string) => `/admin/clinic/${clinicGuid}`,
  adminClinicAdd: '/admin/clinics/add',
  adminClinicEdit: (clinicGuid: string) => `/admin/clinic/${clinicGuid}/edit`,

  // Admin - Patients
  adminPatients: '/admin/patients',
  adminPatient: (patientGuid: string) => `/admin/patient/${patientGuid}`,
  adminPatientAdd: '/admin/patients/add',
  adminPatientEdit: (patientGuid: string) =>
    `/admin/patient/${patientGuid}/edit`,

  // Admin - Users
  adminUsers: '/admin/users',
  adminUser: (userGuid: string) => `/admin/user/${userGuid}`,
  adminUserAdd: '/admin/users/add',
  adminUserEdit: (userGuid: string) => `/admin/user/${userGuid}/edit`,

  // Admin - Turk Users
  adminTurkUserList: '/admin/turk-users',
  adminTurkUser: (agentGuid: string) => `/admin/turk-users/${agentGuid}`,
  adminTurkUsersAdd: '/admin/turk-users/add',
  adminTurkUsersEdit: (agentGuid: string) =>
    `/admin/turk-users/${agentGuid}/edit`,

  // Admin - Other
  adminBulkUpload: '/admin/bulk-upload',
  adminTurkConfigurations: '/admin/turk-configurations',

  // External URLs
  awsConnectURL: awsConnectUrl,
  BAA: 'https://myaccu.net/BAAform',
  telmed: telemedUrl,
  W9: 'https://myaccu.net/w9form',
} as const;

export const GroupedRoutes = {
  adminPanelUsers: [
    Routes.smsDashboard,
    Routes.adminUsers,
    Routes.adminUser(':userGuid'),
    Routes.adminUserEdit(':userGuid'),
    Routes.adminUserAdd,
  ],
  adminPanelAgencyDoctor: [
    Routes.adminAgencyDoctorList,
    Routes.adminAgencyDoctorAdd,
    Routes.adminAgencyDoctor(':agentGuid'),
    Routes.adminAgencyDoctorEdit(':agentGuid'),
  ],
  adminPanelClinics: [
    Routes.adminClinics,
    Routes.adminClinicAdd,
    Routes.adminClinic(':clinicGuid'),
    Routes.adminClinicEdit(':clinicGuid'),
  ],
  adminPanelAgencies: [
    Routes.adminAgencyList,
    Routes.adminAgencyAdd,
    Routes.adminAgency(':agencyGuid'),
    Routes.adminAgencyEdit(':agencyGuid'),
  ],
  adminPanelPatient: [
    Routes.adminPatients,
    Routes.adminPatientAdd,
    Routes.adminBulkUpload,
    Routes.adminPatient(':patientGuid'),
    Routes.adminPatientEdit(':patientGuid'),
    Routes.ccmConsent(':patientGuid'),
    Routes.apcmConsent(':patientGuid'),
  ],
  patientAcceptance: [
    Routes.adminPatients,
    Routes.adminPatient(':patientGuid'),
    Routes.adminPatientEdit(':patientGuid'),
    Routes.ccmConsent(':patientGuid'),
    Routes.apcmConsent(':patientGuid'),
  ],
  profile: [Routes.profile],
  dashboard: [Routes.overviewDashboard],
  ccmDashboard: [Routes.ccm],
  patientDetails: [Routes.patientDetails],
  interactiveTimeDashboard: [Routes.interactiveTimeDashboard],
  ccmAgentDashboard: [Routes.ccmAgentDashboard],
  adherenceSettings: [
    Routes.adherenceSettingsCohortsInactiveDays,
    Routes.adherenceSettingsQueueLimits,
  ],
  reports: [
    Routes.activityReport,
    Routes.adherenceReport,
    Routes.adherenceReportLast30Days,
    Routes.clinicalMetrics,
    Routes.complianceReport,
    Routes.interactiveTime,
    Routes.medicareBilling,
    Routes.interactionStatus,
    Routes.onboardingStatus,
    Routes.patientsStatusReport,
    Routes.performanceReview,
    Routes.statisticsReport,
    Routes.ccmBillingReport,
    Routes.ccmBillingReportV2,
    Routes.ccmInteractiveTimeReport,
    Routes.ccmStatusReport,
    Routes.ccmOnboardingStatus,
    Routes.newPatientsReport,
    Routes.normalReadingsReport,
    Routes.apcmEligibilityReport,
  ],
  rpmAdd: [Routes.servicePlans, Routes.practiceOverview],
  turkAdmin: [
    Routes.adminTurkConfigurations,
    Routes.adminTurkUsersAdd,
    Routes.adminTurkUsersEdit(':agentGuid'),
    Routes.adminTurkUserList,
    Routes.adminTurkUser(':agentGuid'),
  ],
  turkUser: [Routes.turkDashboard],
  smsDashboard: [Routes.smsDashboard, Routes.smsDashboardMetrics],
  smsAutoReplySettings: [Routes.smsAutoReplySettings],
  callDashboard: [Routes.callDashboard],
  patientProfile: [
    Routes.patientGuid(':patientGuid'),
    Routes.ccmPatient(':patientGuid'),
  ],
  adminTools: [
    Routes.eligibilityTool,
    Routes.eligibilityToolItems(':processGuid'),
    Routes.untrackedInboundCalls,
  ],
  callManagement: [Routes.callHistoryManagement],
  billingTools: [Routes.billingClaimsAthena, Routes.billingClaimsEpic],
  patientsTools: [Routes.patientTransfer],
  ccmConsole: [Routes.ccmConsole],
} as const;
